import React, { useEffect, useState } from "react";
import { AdminSkeleton } from "@/components/FeedSkeleton";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { ScrollText, Shield, FileText, LogIn, ShieldCheck, FileDown, RotateCcw } from "lucide-react";

const LANGS = [
  { code: "fr", label: "Français" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "pt", label: "Português" },
];
const DOCS = [
  { key: "terms", label: "Conditions d'utilisation (CGU)", icon: ScrollText },
  { key: "privacy", label: "Politique de confidentialité", icon: Shield },
  { key: "gdpr", label: "Conformité RGPD", icon: FileText },
];
const AUTH_FIELDS = [
  { key: "title", label: "Titre (accroche)", placeholder: "Le réseau chrétien qui rassemble." },
  { key: "subtitle", label: "Sous-titre (description)", placeholder: "Partagez vos versets, priez ensemble…" },
  { key: "login_button", label: "Texte du bouton « Se connecter »", placeholder: "Se connecter" },
  { key: "register_button", label: "Texte du bouton « Créer un compte »", placeholder: "Créer un compte" },
  { key: "demo", label: "Aide « Démo » (sous le formulaire)", placeholder: "Démo : marie@yalola.app / demo1234", hint: "Laisse vide pour MASQUER complètement cette ligne (ex. en production)." },
  { key: "footer", label: "Pied de page (copyright)", placeholder: "© Yalola · Belgique · Conforme RGPD · Fait avec foi", hint: "Laisse vide pour masquer cette ligne. Le pays est remplacé automatiquement par le pays de législation." },
];

export const LegalPanel = ({ isSuper }) => {
  const { lang, setConfig } = useApp();
  const [legal, setLegal] = useState(null);
  const [auth, setAuth] = useState(null);
  const [docType, setDocType] = useState("terms");
  const [docLang, setDocLang] = useState("fr");
  const [savingDoc, setSavingDoc] = useState(false);
  const [savingAuth, setSavingAuth] = useState(false);
  const [consents, setConsents] = useState(null);
  const [consentType, setConsentType] = useState("");

  const loadConsents = () => api.get(`/admin/consents${consentType ? `?type=${consentType}` : ""}`)
    .then(({ data }) => setConsents(data)).catch(() => setConsents({ consents: [], total: 0 }));
  useEffect(() => {
    if (isSuper) loadConsents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consentType, isSuper]);
  const exportConsents = async () => {
    try {
      const res = await api.get(`/admin/consents/export${consentType ? `?type=${consentType}` : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "consentements_yalola.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Erreur d'export"); }
  };

  useEffect(() => {
    api.get("/admin/legal").then(({ data }) => setLegal(data.legal || {})).catch(() => setLegal({}));
    api.get("/admin/settings").then(({ data }) => setAuth(data.settings?.auth_texts || {})).catch(() => setAuth({}));
  }, []);

  const docValue = legal?.[docType]?.[docLang] ?? "";
  const setDocValue = (v) =>
    setLegal((l) => ({ ...l, [docType]: { ...(l[docType] || {}), [docLang]: v } }));

  const saveDoc = async () => {
    if (!isSuper) return;
    setSavingDoc(true);
    try {
      const { data } = await api.patch("/admin/legal", { doc_type: docType, lang: docLang, content: docValue });
      setLegal(data.legal || {});
      toast.success("Document enregistré");
    } catch (e) {
      toast.error(e?.friendlyMessage || "Erreur d'enregistrement");
    } finally { setSavingDoc(false); }
  };

  const setAuthField = (field, code, v) =>
    setAuth((a) => ({ ...a, [field]: { ...(a[field] || {}), [code]: v } }));

  const [resetting, setResetting] = useState("");
  const resetAuthField = async (field) => {
    if (!isSuper) return;
    setResetting(field);
    try {
      const { data } = await api.delete(`/admin/settings/auth-text/${field}`);
      setAuth(data.settings?.auth_texts || {});
      toast.success("Texte rétabli au réglage par défaut de l'application");
      try { const c = await api.get(`/config/public?lang=${lang}`); setConfig(c.data); } catch { /* noop */ }
    } catch (e) {
      toast.error(e?.friendlyMessage || "Erreur");
    } finally { setResetting(""); }
  };

  // Effective state of an auth text (helps distinguish "hidden" from "default").
  const HIDEABLE = new Set(["demo", "footer"]);
  const fieldState = (field, code) => {
    const v = auth?.[field]?.[code];
    if (v === undefined || v === null) return { label: "Par défaut", color: "var(--yalola-text-muted)" };
    if (v === "") return HIDEABLE.has(field)
      ? { label: "Masqué", color: "#B45309" }
      : { label: "Par défaut", color: "var(--yalola-text-muted)" };
    return { label: "Personnalisé", color: "var(--yalola-primary)" };
  };

  const saveAuth = async () => {
    if (!isSuper) return;
    setSavingAuth(true);
    try {
      await api.patch("/admin/settings", { auth_texts: auth });
      toast.success("Textes de connexion enregistrés");
      try { const c = await api.get(`/config/public?lang=${lang}`); setConfig(c.data); } catch { /* noop */ }
    } catch (e) {
      toast.error(e?.friendlyMessage || "Erreur d'enregistrement");
    } finally { setSavingAuth(false); }
  };

  if (legal === null || auth === null) {
    return <AdminSkeleton />;
  }

  return (
    <div className="space-y-4" data-testid="legal-panel">
      {/* Legal documents */}
      <div className="yalola-card p-4">
        <div className="font-semibold text-sm flex items-center gap-1.5"><ScrollText size={16} style={{ color: "var(--yalola-primary)" }} /> Documents légaux</div>
        <p className="text-xs mt-1" style={{ color: "var(--yalola-text-muted)" }}>
          Modifie les CGU, la politique de confidentialité et la page RGPD, dans les 4 langues. Ces textes sont publics via le pied de page. Le HTML simple (titres, listes, gras) est accepté.
        </p>
        <div className="flex flex-wrap gap-1.5 mt-3" data-testid="legal-doctype-tabs">
          {DOCS.map((d) => {
            const Icon = d.icon; const on = docType === d.key;
            return (
              <button key={d.key} onClick={() => setDocType(d.key)}
                className={`pill ${on ? "pill-primary" : "pill-outline"} text-xs`} data-testid={`legal-doctype-${d.key}`}>
                <Icon size={13} className="mr-1" /> {d.label}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="legal-doclang-tabs">
          {LANGS.map((l) => (
            <button key={l.code} onClick={() => setDocLang(l.code)}
              className={`pill ${docLang === l.code ? "pill-primary" : "pill-outline"} text-xs`} data-testid={`legal-doclang-${l.code}`}>
              {l.label}
            </button>
          ))}
        </div>
        <Textarea
          rows={16}
          className="mt-3 font-mono text-xs leading-relaxed"
          value={docValue}
          onChange={(e) => setDocValue(e.target.value)}
          disabled={!isSuper}
          placeholder="Contenu du document (HTML simple)…"
          data-testid="legal-editor"
        />
        <div className="flex justify-end mt-3">
          <Button className="pill pill-primary" onClick={saveDoc} disabled={savingDoc || !isSuper} data-testid="legal-save">
            {savingDoc ? "Enregistrement…" : `Enregistrer (${docLang.toUpperCase()})`}
          </Button>
        </div>
      </div>

      {/* Auth screen texts */}
      <div className="yalola-card p-4">
        <div className="font-semibold text-sm flex items-center gap-1.5"><LogIn size={16} style={{ color: "var(--yalola-primary)" }} /> Écrans de connexion (textes)</div>
        <p className="text-xs mt-1" style={{ color: "var(--yalola-text-muted)" }}>
          Personnalise le titre, le sous-titre et les libellés des boutons de la page de connexion/inscription, dans chaque langue. Laisse vide pour garder le texte par défaut.
        </p>
        <div className="mt-3 space-y-4">
          {AUTH_FIELDS.map((f) => (
            <div key={f.key} data-testid={`auth-text-field-${f.key}`}>
              <div className="flex items-center justify-between gap-2">
                <Label className="text-xs font-semibold">{f.label}</Label>
                {isSuper && (
                  <button
                    type="button"
                    onClick={() => resetAuthField(f.key)}
                    disabled={resetting === f.key}
                    className="text-[10px] font-semibold inline-flex items-center gap-1 hover:underline disabled:opacity-50"
                    style={{ color: "var(--yalola-primary)" }}
                    data-testid={`auth-text-reset-${f.key}`}
                  >
                    <RotateCcw size={11} /> {resetting === f.key ? "…" : "Rétablir par défaut"}
                  </button>
                )}
              </div>
              {f.hint && <p className="text-[10px] mb-1" style={{ color: "var(--yalola-text-muted)" }}>{f.hint}</p>}
              <div className="grid sm:grid-cols-2 gap-2 mt-1">
                {LANGS.map((l) => {
                  const st = fieldState(f.key, l.code);
                  return (
                    <div key={l.code} className="flex items-center gap-2">
                      <span className="text-[10px] font-bold w-7 shrink-0" style={{ color: "var(--yalola-text-muted)" }}>{l.code.toUpperCase()}</span>
                      <div className="flex-1">
                        <Input
                          value={auth?.[f.key]?.[l.code] || ""}
                          onChange={(e) => setAuthField(f.key, l.code, e.target.value)}
                          placeholder={l.code === "fr" ? f.placeholder : ""}
                          disabled={!isSuper}
                          data-testid={`auth-text-${f.key}-${l.code}`}
                        />
                        <span className="text-[9px] font-semibold" style={{ color: st.color }} data-testid={`auth-text-state-${f.key}-${l.code}`}>{st.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-3">
          <Button className="pill pill-primary" onClick={saveAuth} disabled={savingAuth || !isSuper} data-testid="auth-texts-save">
            {savingAuth ? "Enregistrement…" : "Enregistrer les textes"}
          </Button>
        </div>
      </div>

      {/* Consent journal (GDPR) */}
      {isSuper && (
        <div className="yalola-card p-4" data-testid="consents-panel">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="font-semibold text-sm flex items-center gap-1.5"><ShieldCheck size={16} style={{ color: "var(--yalola-primary)" }} /> Journal des consentements (RGPD)</div>
            <Button size="sm" variant="outline" className="rounded-full" onClick={exportConsents} data-testid="consents-export"><FileDown size={14} className="mr-1" /> Exporter CSV</Button>
          </div>
          <p className="text-xs mt-1" style={{ color: "var(--yalola-text-muted)" }}>
            Historique des acceptations de cookies et des CGU par membre. Total : {consents?.total ?? 0}.
          </p>
          <div className="flex gap-1.5 mt-2">
            {[["", "Tous"], ["cookies", "Cookies"], ["terms", "CGU"]].map(([v, l]) => (
              <button key={v || "all"} onClick={() => setConsentType(v)} className={`pill ${consentType === v ? "pill-primary" : "pill-outline"} text-xs`} data-testid={`consents-filter-${v || "all"}`}>{l}</button>
            ))}
          </div>
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-xs">
              <thead><tr style={{ color: "var(--yalola-text-muted)" }}>
                <th className="text-left py-1">Date</th><th className="text-left">Type</th><th className="text-left">Valeur</th><th className="text-left">Membre</th><th className="text-left">Catégories</th>
              </tr></thead>
              <tbody>
                {(consents?.consents || []).map((c) => (
                  <tr key={c.id} className="border-t" style={{ borderColor: "var(--yalola-border)" }} data-testid={`consent-row-${c.id}`}>
                    <td className="py-1.5">{new Date(c.created_at).toLocaleString("fr-FR")}</td>
                    <td><span className="chip text-[10px]">{c.type}</span></td>
                    <td>{c.value}</td>
                    <td>{c.user?.email || c.user_id}</td>
                    <td>{c.categories ? (Object.entries(c.categories).filter(([, v]) => v).map(([k]) => k).join(", ") || "—") : "—"}</td>
                  </tr>
                ))}
                {(!consents?.consents || consents.consents.length === 0) && (
                  <tr><td colSpan={5} className="py-4 text-center" style={{ color: "var(--yalola-text-muted)" }}>Aucun consentement enregistré.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!isSuper && <p className="text-xs text-center" style={{ color: "var(--yalola-text-muted)" }}>Seul le Super Admin peut modifier ces textes.</p>}
    </div>
  );
};

export default LegalPanel;
